import type { Geometry } from "geojson";
import type mapboxgl from "mapbox-gl";

import darkThumbnail from "../../../assets/images/map-tools/dark_thumbnail.svg";
import lightThumbnail from "../../../assets/images/map-tools/light_thumbnail.svg";
import satelliteThumbnail from "../../../assets/images/map-tools/satellite_thumbnail.svg";
import topographyThumbnail from "../../../assets/images/map-tools/topography_thumbnail.svg";

/** Minimal shape the panel needs from a field. `FieldDataInterface` satisfies it. */
export interface FieldData {
  id: string;
  name: string;
  geometry?: Geometry;
}

export interface MapControlsPanelProps {
  /** Live map instance, e.g. `mapRef.current?.getMap()`. */
  mapRef?: mapboxgl.Map | null;
  /** Field currently selected for the trial — rendered highlighted by the field-names tool. */
  currentFieldId?: string;
  /** Every field on the property, used to label neighbouring fields. */
  neighboringFields?: FieldData[];
}

export interface BaseLayerType {
  id: string;
  /** English fallback; the panel renders `newTrial.mapControls.baseLayers.<id>`. */
  name: string;
  style: string;
  thumbnail: string;
}

export const BASE_LAYERS: BaseLayerType[] = [
  {
    id: "satellite",
    name: "Satellite",
    style: "mapbox://styles/mapbox/satellite-v9",
    thumbnail: satelliteThumbnail,
  },
  {
    id: "streets",
    name: "Light",
    style: "mapbox://styles/mapbox/streets-v12",
    thumbnail: lightThumbnail,
  },
  {
    id: "hybrid",
    name: "Dark",
    style: "mapbox://styles/mapbox/satellite-streets-v12",
    thumbnail: darkThumbnail,
  },
  {
    id: "terrain",
    name: "Topography",
    style: "mapbox://styles/mapbox/outdoors-v12",
    thumbnail: topographyThumbnail,
  },
];

/**
 * Source and layer ids owned by the map tools. Prefixed so they can never
 * collide with the trial design layers already on the map.
 */
export const MEASURE_SOURCE_ID = "map-tool-measure-source";
export const MEASURE_FILL_LAYER_ID = "map-tool-measure-fill";
export const MEASURE_LINE_LAYER_ID = "map-tool-measure-line";
export const MEASURE_POINTS_LAYER_ID = "map-tool-measure-points";
export const MEASURE_FIRST_POINT_LAYER_ID = "map-tool-measure-first-point";

export const FIELD_LABELS_SOURCE_ID = "map-tool-field-labels";
export const FIELD_LABELS_LAYER_ID = "map-tool-field-labels-layer";

/**
 * The trial map already draws a field-name label per field. The field names
 * toggle drives this layer so labels are never rendered twice.
 */
export const EXISTING_FIELD_NAME_LAYER_ID = "polygon-name";

/** Road label layers shipped by the Mapbox base styles. */
export const ROAD_LABEL_LAYER_IDS = [
  "road-label",
  "road-label-simple",
  "road-number-shield",
  "road-exit-shield",
];

export type MeasurementType = "distance" | "area";

export interface Measurement {
  value: number;
  unit: string;
  type: MeasurementType;
  /** Only set for area measurements — perimeter of the drawn polygon. */
  perimeter?: number;
  perimeterUnit?: string;
}

export interface LngLatPoint {
  lng: number;
  lat: number;
}
